import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { IconComponent } from './ui/icon.component';
import { WhatsappModalService } from '../services/whatsapp-modal.service';

@Component({
  selector: 'app-whatsapp-modal',
  standalone: true,
  imports: [CommonModule, IconComponent, FormsModule],
  template: `
    @if (whatsappModalService.isOpen()) {
      <div class="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <!-- Backdrop -->
        <div 
          class="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
          (click)="close()"
        ></div>

        <!-- Modal Content -->
        <div 
          class="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-300 scale-100 flex flex-col animate-fade-in"
          role="dialog"
          aria-modal="true"
        >
          <!-- Button Close -->
          <button 
            (click)="close()" 
            class="absolute top-4 right-4 z-10 p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-100"
          >
            <app-icon name="x" [size]="24"></app-icon>
          </button>

          <div class="p-6 md:p-8">
            <div class="text-center mb-6">
              <div class="w-16 h-16 bg-[#25D366]/10 rounded-full flex items-center justify-center mx-auto mb-4 text-[#25D366]">
                <app-icon name="whatsapp" [size]="32"></app-icon>
              </div>
              <h3 class="text-2xl font-bold text-gray-900 mb-2">Fale com um Especialista</h3>
              <p class="text-gray-600 text-sm">
                Preencha seus dados abaixo para ser redirecionado ao nosso WhatsApp.
              </p>
            </div>

            <form (ngSubmit)="onSubmit()" #waForm="ngForm" class="space-y-4">
              <div>
                <label for="wa-nome" class="block text-sm font-medium text-gray-700 mb-1">Nome completo *</label>
                <input 
                  type="text" 
                  id="wa-nome" 
                  name="nome"
                  [(ngModel)]="nome"
                  required
                  placeholder="Seu nome"
                  class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#FF5500] focus:border-[#FF5500] outline-none transition-all text-gray-900"
                >
              </div>

              <div>
                <label for="wa-telefone" class="block text-sm font-medium text-gray-700 mb-1">WhatsApp *</label>
                <input 
                  type="tel" 
                  id="wa-telefone" 
                  name="telefone"
                  [ngModel]="telefone"
                  (ngModelChange)="onPhoneChange($event)"
                  required
                  minlength="14"
                  maxlength="15"
                  placeholder="(XX) XXXXX-XXXX"
                  class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#FF5500] focus:border-[#FF5500] outline-none transition-all text-gray-900"
                >
              </div>

              <button 
                type="submit" 
                [disabled]="waForm.invalid || isSubmitting()"
                class="w-full mt-6 bg-[#25D366] hover:bg-[#1ebe5d] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3.5 px-6 rounded-xl flex items-center justify-center gap-2 transition-all duration-200"
              >
                @if (isSubmitting()) {
                  <span class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  Enviando...
                } @else {
                  Continuar para o WhatsApp
                  <app-icon name="arrow-right" [size]="18"></app-icon>
                }
              </button>
            </form>
          </div>
        </div>
      </div>
    }
  `,
  styles: [`
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px) scale(0.95); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
    .animate-fade-in {
      animation: fadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    }
  `]
})
export class WhatsappModalComponent {
  whatsappModalService = inject(WhatsappModalService);
  private router = inject(Router);

  nome = '';
  telefone = '';
  isSubmitting = signal(false);

  close() {
    this.whatsappModalService.close();
    this.nome = '';
    this.telefone = '';
  }

  onPhoneChange(value: string) {
    let digits = value.replace(/\D/g, '').slice(0, 11);
    
    // Mask logic
    let masked = '';
    if (digits.length > 0) {
      masked = '(' + digits.slice(0, 2);
      if (digits.length > 2) {
        masked += ') ' + digits.slice(2, 7);
        if (digits.length > 7) {
          masked += '-' + digits.slice(7, 11);
        }
      }
    }
    this.telefone = masked;
  }

  async onSubmit() {
    if (!this.nome || !this.telefone) return;

    this.isSubmitting.set(true);

    const payload = {
      nome: this.nome,
      telefone: this.telefone
    };

    try {
      await fetch('https://script.google.com/macros/s/AKfycbyMWyQ7y6-m-B9cARQ4G6B3u3j_VX9zMHdxwTRr75u9H_a2MU7rWUvcYbT4ONlCN6e3/exec', {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain;charset=utf-8',
        },
        body: JSON.stringify(payload),
        mode: 'no-cors'
      });
    } catch (error) {
      console.error('Erro ao salvar lead do WhatsApp:', error);
    } finally {
      this.isSubmitting.set(false);
      const targetUrl = this.whatsappModalService.targetUrl();
      this.close();
      
      const isV2 = this.router.url.includes('/v2');
      const targetRoute = isV2 ? '/v2/obrigado' : '/obrigado';
      
      this.router.navigate([targetRoute], { state: { source: 'wa_modal', targetUrl } });
    }
  }
}
